<template>
  <div class="tours-container">
    <!-- <script type="application/ld+json" v-html="itemListJsonLd"></script> -->
    <link href="https://fonts.googleapis.com/css2?family=Noto+Sans&family=Roboto+Slab&display=swap" rel="stylesheet">
    <div class="tours-header">
    </div>
    
    <div class="tour-categories">
      <div class="category-tabs">
        <button 
          v-for="(category, key) in tourCategories" 
          :key="key"
          :class="['category-tab', { active: activeCategory === key }]"
          @click="activeCategory = key"
        >
          {{ category.name }}
        </button>
      </div>
      
      <div class="category-description">
        <h2>{{ tourCategories[activeCategory].name }}</h2>
        <p>{{ tourCategories[activeCategory].description }}</p>
      </div>
    </div>
    
    <div class="tours-grid">
      <router-link 
              v-for="tour in tourData[activeCategory]" 
              :key="tour.id" 
              :to="`/tours/${activeCategory}/${tour.id}`"
              class="tour-card"
            >
            <img :src="tour.image" :alt="tour.title">
            <div class="tour-overlay">
              <div class="tour-overlay-content">
                <h2>{{ tour.title }}</h2>
                <p>{{ tour.subtitle }}</p>
              </div>
            </div>
    </router-link>
    </div>
  </div>
  <TestimonialsSection class="title-red-background" />
  <TravelerMemories :altBackground="false" />
</template>

<script setup>
import { ref, computed, watch, getCurrentInstance, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useHead } from '@vueuse/head';
import TestimonialsSection from '../components/TestimonialsSection.vue';
import TravelerMemories from '../components/TravelerMemories.vue';
import { tourData, tourCategories } from '../data/tourData';

const route = useRoute();
const router = useRouter();
const { proxy } = getCurrentInstance();

// Set default active category based on route query param or default to ClassicTour
const activeCategory = ref('ClassicTour');

// Update active category when route query changes
watch(
  () => route.query.category,
  (newCategory) => {
    if (newCategory && tourCategories[newCategory]) {
      activeCategory.value = newCategory;
    }
  },
  { immediate: true }
);

// Update URL when active category changes
watch(
  () => activeCategory.value,
  (newCategory) => {
    if (newCategory !== route.query.category) {
      router.push({
        path: '/tours',
        query: { category: newCategory }
      });
    }
  }
);

onMounted(() => {
  proxy.$meta.update({
    title: 'Yunnan Tours - Best Private & Customizable Yunnan Tour Packages',
    description: 'Browse our best Yunnan tours, including classic, tea, trekking, and short day tours. 100% tailor-made for you. Discover authentic experiences in Yunnan with local experts.',
    image: '/images/tours/background.jpg',
    canonicalUrl: 'https://realyunnan.com/tours/',
    ogTitle: 'Yunnan Tours - Best Private & Customizable Yunnan Tour Packages',
    ogDescription: 'Browse our best Yunnan tours, including classic, tea, trekking, and short day tours. 100% tailor-made for you. Discover authentic experiences in Yunnan with local experts.',
    ogImage: '/images/tours/background.jpg',
    ogUrl: 'https://realyunnan.com/tours/'
  });
});

const itemListJsonLd = computed(() => {
  const tours = tourData[activeCategory.value] || [];
  return JSON.stringify({
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Yunnan Tours",
    "itemListElement": tours.map((tour, idx) => ({
      "@type": "TouristTrip",
      "position": idx + 1,
      "name": tour.title,
      "description": tour.subtitle,
      "image": tour.image,
      "url": `https://realyunnan.com/tours/${activeCategory.value}/${tour.id}`
    }))
  });
});

// 用 @vueuse/head 注入 JSON-LD
useHead({
  script: [
    {
      type: 'application/ld+json',
      children: itemListJsonLd
    }
  ]
});
</script>

<style scoped>
.tours-container {
  width: 100%;
  min-height: calc(100vh - 70px); /* 减去导航栏高度 */
  background-color: var(--color-background-soft);
}

.tours-header {
  text-align: center;
  padding: 4rem 1rem;
  background: url('/images/tours/background.jpg');
  background-size: cover;
  background-position: center;
  color: white;
  margin-bottom: 2rem;
  min-height: 450px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.tour-categories {
  max-width: 1200px;
  margin: 0 auto 2rem;
  padding: 0 2rem;
}

.category-tabs {
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
}

.category-tab {
  padding: 0.75rem 1.5rem;
  background: white;
  border: none;
  border-radius: 30px;
  font-size: 1rem;
  font-weight: 500;
  color: #555;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 10px rgba(0,0,0,0.05);
}

.category-tab:hover {
  transform: translateY(-3px);
  box-shadow: 0 5px 15px rgba(0,0,0,0.1);
}

.category-tab.active {
  background: #008c5c;
  color: white;
}

.category-description {
  text-align: center;
  max-width: 1200px;
  margin: 0 auto 2rem;
}

.category-description h2 {
  color: #333;
  margin-bottom: 0.5rem;
  font-size: 2.5rem;
  font-weight: bold;
  font-family: serif;
}

.category-description p {
  color: #666;
  font-size: 1rem;
}

.tours-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 2rem;
  padding: 0 2rem 3rem;
  max-width: 1400px;
  margin: 0 auto;
}

.tour-card {
  background: var(--color-background);
  border-radius: 12px;
  overflow: hidden;
  position: relative;
  height: 400px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
  transition: transform 0.3s ease;
}

.tour-card:hover {
  transform: translateY(-10px);
}

.tour-card img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
}

.tour-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  padding: 2rem;
  color: white;
  text-align: center;
  border-radius: 4px;
}

.tour-overlay-content {
  background-color: rgb(0 0 0 / 27%);
  border-radius: 4px;
  display: inline-block; /* Optional: shrink to fit content */
}

.tour-overlay h2 {
  font-size: 1.8rem;
}

.tour-overlay p {
  margin-bottom: 0.5rem;
  font-size: 1rem;
  opacity: 0.9;
  
}


/* .btn {
  display: inline-block;
  padding: 0.75rem 1.5rem;
  background: #008c5c;
  color: white;
  border-radius: 30px;
  text-decoration: none;
  transition: all 0.3s ease;
  font-weight: 500;
} */

.btn:hover {
  background: #00734b;
  transform: translateY(-3px);
}

@media (max-width: 768px) {
  .tours-header {
    padding: 3rem 1rem;
    min-height: 300px;
  }

  .category-tabs {
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
  }
  
  .category-tab {
    width: 100%;
    max-width: 300px;
  }

  .tours-grid {
    grid-template-columns: 1fr;
    padding: 0 1rem 2rem;
    gap: 1.5rem;
  }

  .tour-card {
    height: 350px;
  }
}

@media (max-width: 480px) {
  .tours-header {
    padding: 2.5rem 0.5rem;
    min-height: 250px;
  }

  .category-description h2 {
    font-size: 1.5rem;
  }

  .category-description p {
    font-size: 1rem;
  }

  .tours-grid {
    padding: 0 0.5rem 1.5rem;
  }

  .tour-card {
    height: 300px;
  }
  
  .tour-overlay h2 {
    font-size: 1.5rem;
  }
  
  .tour-overlay p {
    font-size: 0.9rem;
  }
}
</style> 